import React, { useState, useEffect } from 'react';
import { Button, Grid } from '@material-ui/core';
import {
  FormikAutocompletePx,
  FormikTextField,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useFormik } from 'formik';
import { useStyles } from '@webapps/styles';
import axios from 'axios';
import * as yup from 'yup';
import PropTypes from 'prop-types';

const competenceSchema = yup.object().shape({
  job: yup.object().required('Sélectionnez un métier'),
  competence: yup.object().required('Sélectionnez une compétence'),
});

const AddCompetence = ({ onSave, onClose }) => {
  const {
    css,
    theme: { spacing },
  } = useStyles();

  const { addToast } = useToast();

  const [jobs, setJobs] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingCompetences, setLoadingCompetences] = useState(false);

  // Fetch jobs from France Travail API
  const fetchJobs = async (query) => {
    if (!query || query.length < 3) return setJobs([]);
    setLoadingJobs(true);

    try {
      const response = await axios.get(
        `https://api.francetravail.io/rome-metiers?search=${query}`
      );
      setJobs(
        response.data.map((job) => ({
          id: job.code,
          label: job.libelle,
        }))
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des métiers:', error);
      addToast({
        status: 'error',
        message: "Une erreur s'est produite lors de la récupération des métiers.",
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  // Fetch competences for a job
  const fetchCompetences = async (jobId) => {
    if (!jobId) return setCompetences([]);
    setLoadingCompetences(true);

    try {
      const response = await axios.get(
        `https://api.francetravail.io/rome-competences?code_rome=${jobId}`
      );
      setCompetences(
        response.data.map((competence) => ({
          id: competence.code,
          label: competence.libelle,
        }))
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des compétences:', error);
      addToast({
        status: 'error',
        message:
          "Une erreur s'est produite lors de la récupération des compétences.",
      });
    } finally {
      setLoadingCompetences(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      job: null,
      competence: null,
      level: '',
    },
    validationSchema: competenceSchema,
    onSubmit: (values) => {
      onSave(values);
      addToast({
        status: 'success',
        message: 'Compétence ajoutée avec succès !',
      });
      onClose();
    },
  });

  useEffect(() => {
    if (formik.values.job?.id) {
      fetchCompetences(formik.values.job.id);
    }
  }, [formik.values.job]);

  return (
    <div>
      <Grid container spacing={2}>
        {/* Select Job */}
        <Grid item xs={12}>
          <FormikAutocompletePx
            formik={formik}
            fullWidth
            borderRadius={6}
            label="Métier"
            name="job"
            getOptionLabel={(option) => option.label || ''}
            options={jobs}
            loading={loadingJobs}
            onInputChange={(e, value) => fetchJobs(value)}
            value={jobs.find((job) => job.id === formik.values.job?.id) || null}
          />
        </Grid>

        {/* Select Competence */}
        <Grid item xs={12}>
          <FormikAutocompletePx
            formik={formik}
            fullWidth
            borderRadius={6}
            label="Compétence"
            name="competence"
            getOptionLabel={(option) => option.label || ''}
            options={competences}
            loading={loadingCompetences}
            value={
              competences.find(
                (competence) => competence.id === formik.values.competence?.id
              ) || null
            }
          />
        </Grid>

        {/* Level Input */}
        <Grid item xs={12}>
          <FormikTextField
            formik={formik}
            fullWidth
            borderRadius={6}
            label="Niveau (1 à 5)"
            name="level"
            type="number"
            placeholder="1 à 5"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} justifyContent="flex-end" style={{ marginTop: spacing(2) }}>
        <Grid item>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Annuler
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={formik.handleSubmit}
            variant="contained"
            color="primary"
            disabled={loadingJobs || loadingCompetences}
          >
            Enregistrer
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

AddCompetence.propTypes = {
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddCompetence;
